import { SiteGatewayZonePricingPlan } from 'src/features/pricing/sites/utils/types';
import { CURRENCY } from 'src/types';

export enum BILLING_TYPE {
    POST = 'post',
    PRE = 'pre',
}

export enum CALCULATION_TYPE {
    PER_FIXED_TICK = 'per_fixed_tick',
    PER_PERIODS_COMPOUND = 'per_periods_compound',
    PER_PERIODS_TOTAL = 'per_periods_total',
    FIXED_PRICE = 'fixed_price',
}

export enum PRICING_PLAN_STATUS {
    DRAFT = 'draft',
    PUBLISHED = 'published',
}

export enum FREE_TIME_TYPE {
    EVERY_VISIT = 'every_visit',
    CAPPED = 'capped',
}

export enum CAPPING {
    NONE = 'none',
    ONE_TIME = 'one_time',
    REPEATING = 'repeating',
}

export enum TICK_TYPE {
    MINUTE = 'minute',
    HOUR = 'hour',
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
}

type AdministrativeTranslation = {
    id: string;
    created_at: string;
    modified_at: string;
    language: string;
    name: string;
    description: string;
};

export type PricingPlan = {
    id: string;
    translations: AdministrativeTranslation[];
    created_at: string;
    modified_at: string;
    billing_type: BILLING_TYPE;
    status: PRICING_PLAN_STATUS;
    calculation_type: CALCULATION_TYPE;
    version_number: number;
    valid_from: string;
    valid_to: string;
    tax_percent: string;
    currency: CURRENCY;
    free_time_type: FREE_TIME_TYPE;
    free_time_cap_minutes: number | null;
    free_time_minutes: number | null;
    exit_time: number | null;
    exit_time_grace: number | null;
    capping: CAPPING;
    is_current_version: boolean;
    is_system: boolean;
    identity: string;
    previous_version: PricingVersion | null;
    next_version: PricingVersion | null;
    tick: {
        tick_type: TICK_TYPE;
        tick_minutes: number;
    };
    fixed_rate?: {
        rate_with_tax: number;
    };
    extra_free_time: boolean | null;
    extra_free_time_minutes: number | null;
    extra_free_time_activation_remaining_minutes: number | null;
};

export enum PAYMENT_TYPE {
    CARD = 'card',
    BANK = 'bank',
    CASH = 'cash',
    WEB = 'web',
}

export type PricingPlanPeriodAdjustment = {
    id: string;
    created_at: string;
    modified_at: string;
    payment_type: PAYMENT_TYPE;
    valid_from: string;
    valid_to: string | null;
    day_of_week: DAY_OF_WEEK | null;
    from_time: string | null;
    to_time: string | null;
    adjustment_percent: string | null;
    adjustment_amount: number | null;
};

export enum DAY_OF_WEEK {
    MONDAY = 'monday',
    TUESDAY = 'tuesday',
    WEDNESDAY = 'wednesday',
    THURSDAY = 'thursday',
    FRIDAY = 'friday',
    SATURDAY = 'saturday',
    SUNDAY = 'sunday',
}

export type PricingVersion = {
    id: string;
    version_number: number;
    valid_from: string;
    valid_to: string | null;
};

export type PricingPlanTickRate = {
    id: string;
    created_at: string;
    modified_at: string;
    day_of_week: DAY_OF_WEEK;
    from_time: string;
    to_time: string;
    rate_with_tax: number;
    pricing_plan: string;
    pricing_plan_tick: string;
    currency: CURRENCY;
};

export type PricingPlanCap = {
    id: string;
    created_at: string;
    modified_at: string;
    cap_type: TICK_TYPE;
    cap_minutes: number;
    rate_with_tax: number;
};

export type PricingPlanTier = {
    id: string;
    order: number;
    created_at: string;
    modified_at: string;
    start_minute: number;
    end_minute: number | null;
    rate_with_tax: number;
    tick_type: TICK_TYPE;
    tick_minutes: number;
};

export enum USED_IN_TYPE {
    SITE = 'site',
    GATEWAY = 'gateway',
    ZONE = 'zone',
}

export interface PricingPlanUsedInColumns extends SiteGatewayZonePricingPlan {
    type: USED_IN_TYPE;
    translationName?: string;
    zone?: string;
    gateway?: string;
    site?: string;
}
